import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import StudentGapTask from 'Components/ChecklistTasks/StudentGapTask';
import { iclApi, taskApi } from '../../api';
import StudentExcludesTask from '../../Components/ChecklistTasks/StudentExludesTask';
import StudentSelectOneTask from '../../Components/ChecklistTasks/StudentSelectOneTask';
import StudentTestTask from 'Components/ChecklistTasks/StudentTestTask';
import StudentTaskWithLink from '../../Components/ChecklistTasks/StudentTaskWithLink';
import StudentTextTask from '../../Components/ChecklistTasks/StudentTextTask';
import EditorProvider from '../../Components/ImageEditor/EditorProvider';
import { LoadingError } from '../../Components/LoadingError';
import { GapWord, ICL, TaskKeysType, WorkType } from '../../shared/types';
import { ExcludeAnswer, SelectOneAnswer, SummarizeTask, TestItem } from '../../shared/types/task';
import CongratulationsModal from 'Components/CongratulationsModal/CongratulationsModal';
import { useDispatch, useSelector } from 'react-redux';
import { getTotalPoint, setTotalPoint } from 'redux/slices/flagsSlice';

// off tasks
import StudentMathFormulasTask from 'Components/ChecklistTasks/StudentMathFormulasTask';
import StudentSentenceStructureTask from '../../Components/ChecklistTasks/StudentSentenceStructure';
import StudentUnderlineTask from 'Components/ChecklistTasks/StudentUnderlineTask';
import StudentWordCompositionTask from 'Components/ChecklistTasks/StudentWordCompositionTask';
import StudentWordStressTask from 'Components/ChecklistTasks/StudentWordStressTask';

const componentMapping = {
  text: StudentTextTask,
  link: StudentTaskWithLink,
  test: StudentTestTask,
  exclude: StudentExcludesTask,
  selectOne: StudentSelectOneTask,
  gap: StudentGapTask,

  // sentenceStructure: StudentSentenceStructureTask,
  // mathFormulas: StudentMathFormulasTask,
  // wordComposition: StudentWordCompositionTask,
  // wordStress: StudentWordStressTask,
  // underline: StudentUnderlineTask,
};

interface LocationState {
  checkListId?: string;
}

export default function ChecklistStudent() {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();

  const location = useLocation();
  const state = location.state as LocationState | null;
  const checkListId: string | null = state?.checkListId ? state?.checkListId : null;

  const totalPoint = useSelector(getTotalPoint);

  const dispatch = useDispatch();

  const [isComplete, setIsComplete] = useState(false);

  const [checkedAtTaskIds, setCheckedAtTaskIds] = useState<string[] | []>([]);

  const [iclData, setIclData] = useState<ICL | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [tasks, setTasks] = useState<SummarizeTask[]>([]);

  const getTotalPointFromTasks = () => {
    return tasks.filter(task => task.isCorrectAnswer).reduce((total, task) => total + (task.points || 0), 0);
  };

  useEffect(() => {
    dispatch(setTotalPoint(getTotalPointFromTasks()));
  }, [tasks]);

  const isAllTasksCompleted = (): boolean =>
    tasks.length > 0 &&
    tasks.every(
      ({ checkedAt, completedAt }) =>
        typeof checkedAt === 'string' &&
        checkedAt.trim() !== '' &&
        typeof completedAt === 'string' &&
        completedAt.trim() !== '',
    );

  useEffect(() => {
    if (tasks.length > 0) {
      setIsComplete(isAllTasksCompleted());
    }
  }, [tasks]);

  useEffect(() => {
    setLoading(true);

    if (checkListId) {
      iclApi.getICL(checkListId).then(data => {
        if (data) {
          setIclData(data);
        }
      });
    }
    setLoading(false);
  }, [state]);

  useEffect(() => {
    if (checkListId) {
      const updateTasks = (tasks: SummarizeTask[]) => setTasks(tasks);
      const unsubscribe = taskApi.subscribeToICLTasks({ ICLId: checkListId as string, setNewTasks: updateTasks });

      return () => {
        unsubscribe();
      };
    }
  }, [state]);

  const handleChangeTaskByKey = (
    id: string,
    key: TaskKeysType,
    value: string[] | string | boolean | ExcludeAnswer[] | TestItem[] | SelectOneAnswer[] | GapWord[],
  ) => {
    const newTaskArray: SummarizeTask[] = [...tasks];
    const index = newTaskArray.findIndex(obj => obj.id === id);

    const updatedObject: SummarizeTask = { ...newTaskArray[index], [key]: value, isCorrectAnswer: false };

    newTaskArray.splice(index, 1, updatedObject);

    if (key === 'completedAt' && (updatedObject.completedAt || !updatedObject.completedAt)) {
      if (checkListId) taskApi.updateTask<SummarizeTask>(checkListId as string, updatedObject);
    }

    setTasks(newTaskArray);
  };

  const handleAddIDS = () => {
    const ids: string[] = [];

    tasks?.forEach(task => {
      if (task.checkedAt !== '') {
        task.answerOptions?.forEach(item => {
          if (item.lineId && !ids.includes(item.lineId)) {
            ids.push(item.lineId);
          }
        });
      }
    });

    setCheckedAtTaskIds(prevIds => {
      const allIds = [...prevIds, ...ids];
      return Array.from(new Set(allIds));
    });
  };

  useEffect(() => {
    handleAddIDS();
  }, [tasks]);

  const saveTaskAnswer = async (id: string) => {
    setLoading(true);

    const newTaskArray: SummarizeTask[] = [...tasks];
    const index = newTaskArray.findIndex(obj => obj.id === id);

    const updatedObject: SummarizeTask = { ...newTaskArray[index] };

    newTaskArray.splice(index, 1, updatedObject);

    if (checkListId)
      await taskApi.updateTask<SummarizeTask>(checkListId, updatedObject).then(() => {
        setLoading(false);
        toast.success(t('answer saved'));
      });
  };

  const renderTasks = () => {
    return tasks.map((it: SummarizeTask, index: number) => {
      const Component = componentMapping[it.type];

      return (
        <Component
          index={index}
          checkedIds={checkedAtTaskIds}
          icl={iclData}
          key={it.id}
          {...it}
          handleChangeTaskByKey={handleChangeTaskByKey}
          saveTaskAnswer={saveTaskAnswer}
          workTaskType={WorkType.icl}
        />
      );
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      {tasks.length > 0 ? (
        <EditorProvider>
          <LoadingError isLoading={isLoading && !tasks}>
            <div className="mx-auto rounded-[24px] border-x-[8px] border-b-[8px] border-[#9CDFFC7F] border-opacity-50 bg-white px-3 py-4 shadow-none lg:w-11/12 lg:!px-[120px] lg:!py-[40px]">
              <div className="flex w-fit flex-col gap-[12px] lg:w-full lg:gap-[20px]">
                <div className="lg:leading-12 text-[24px] font-bold leading-9 lg:text-[48px]">
                  {t('checklist')} №<span>{iclData?.number}</span>
                </div>

                <div className="text-[18px] font-bold leading-6 lg:text-[24px] lg:leading-8">
                  {t('theme')}: <span className="font-medium">{iclData?.theme}</span>
                </div>
              </div>

              <div>{tasks.length > 0 && renderTasks()}</div>

              {isComplete && <CongratulationsModal setIsCompleted={setIsComplete} />}
            </div>
          </LoadingError>
        </EditorProvider>
      ) : (
        <div className="mx-auto max-w-[600px] text-center text-[24px] font-bold leading-[2rem] tracking-wide lg:text-[48px] lg:leading-[4rem]">
          {t('deletedIcl')}
        </div>
      )}
    </div>
  );
}
